import React, { useState, useRef, useEffect } from "react";
import { MdCheckCircle } from "react-icons/md";

const CustomBoloSlider2 = ({
  defaultValue = 1000,
  min = 500,
  max = 5000,
  marks = [
    { value: 500, label: "500" },
    { value: 1000, label: "1000" },
    { value: 2000, label: "2000" },
    { value: 5000, label: "5000" },
  ],
  onChange,
  currentActiveMark = 0,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  // Find the closest mark value to snap to
  const findClosestMark = (currentValue) => {
    return marks.reduce((closest, mark) => {
      return Math.abs(mark.value - currentValue) <
        Math.abs(closest - currentValue)
        ? mark.value
        : closest;
    }, marks[0].value);
  };

  // Convert pixel position to value
  const getValueFromPosition = (position) => {
    if (!sliderRef.current) return min;
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = (position - rect.left) / rect.width;
    const rawValue = min + (max - min) * Math.max(0, Math.min(1, percentage));
    return Math.round(rawValue);
  };

  // Convert value to percentage for positioning
  const getPercentage = (value) => {
    if (value === 200) {
      return ((value + 80 - min) / (max - min)) * 100;
    }

    return ((value - min) / (max - min)) * 100;
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const newValue = getValueFromPosition(e.clientX);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newValue = getValueFromPosition(e.clientX);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const snappedValue = findClosestMark(value);
    setValue(snappedValue);
    if (onChange) onChange(snappedValue);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, value]);

  return (
    <div className="w-full py-8 select-none">
      {/* Slider track */}
      <div
        ref={sliderRef}
        className="relative w-full h-2 bg-slate-200 cursor-pointer"
        onMouseDown={handleMouseDown}
      >
        {/* Active track */}
        <div
          className="absolute h-2 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 "
          style={{ width: `${getPercentage(value)}%` }}
        />

        {/* Marks */}
        {marks.map((mark, index) => (
          <div
            key={mark.value}
            style={{ left: `${getPercentage(mark.value)}%` }}
            className="absolute"
          >
            <div
              className={`absolute md:w-1 w-0.5 h-6 rounded-full   -translate-x-1/2 -translate-y-2 ${
                mark.value <= value ? "bg-primary" : "bg-slate-400"
              } `}
            />
            <div
              className={`absolute text-xs -translate-x-1/2 mt-6 flex flex-col gap-1 items-center justify-center ${
                mark.value <= value ? "text-primary font-bold" : "text-gray-600"
              } `}
            >
              {mark.label}
            </div>
          </div>
        ))}

        {/* Thumb */}
        <div
          ref={thumbRef}
          className={`absolute w-6 h-6 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 rounded-full border-[3px] border-white shadow-md cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-150 top-1/2 `}
          style={{
            left: `${getPercentage(value)}%`,
            transform: `translate(-50%, -50%) scale(${isDragging ? 1.1 : 1})`,
          }}
        />
      </div>
    </div>
  );
};

export default CustomBoloSlider2;
